// TreemapChart.js
import React from 'react';
import { Box, CircularProgress } from '@mui/material';
import ReactApexChart from 'react-apexcharts';

const TreemapChart = ({
    categories,
    totalCounts,
    onDataPointClick,
    isLoading
}) => {



    const colorPalette = [
        '#D0CEFB',
        '#FEB4AA',
        '#B8DFFE',
        '#FCE7B8',
        '#FCD4E3',
    ];

    const getColorForCategory = (categoryName, index) => {
        return colorPalette[index % colorPalette.length];
    };

    const categoryEntries = categories ? Object.entries(categories) : [];
    const sortedEntries = [...categoryEntries].sort((a, b) => (b[1]?.count || 0) - (a[1]?.count || 0));

    const indexMapping = {};
    sortedEntries.forEach((entry, sortedIndex) => {
        const originalIndex = categoryEntries.findIndex(([key]) => key === entry[0]);
        indexMapping[sortedIndex] = originalIndex;
    });

    const seriesData = sortedEntries.map(([key, value], index) => {
        const color = getColorForCategory(key, index);

        return {
            x: key.replace(/_/g, ' '),
            y: value?.count || 1,
            fillColor: color,
            count: value?.count || 0,
            originalIndex: indexMapping[index],
            categoryKey: key,
            categoryData: value
        };
    });

    const totalValue = seriesData.reduce((sum, item) => sum + item.y, 0);

    const handleDataPointSelection = (event, chartContext, config) => {
        if (onDataPointClick && config.dataPointIndex !== undefined) {
            const clickedData = seriesData[config.dataPointIndex];
            if (clickedData) {
                onDataPointClick(event, chartContext, {
                    dataPointIndex: clickedData.originalIndex,
                    selectedCategory: clickedData.categoryKey,
                    categoryData: [clickedData.categoryKey, clickedData.categoryData]
                });
            }
        }
    };

    const chartOptions = {
        chart: {
            height: '100%',
            type: 'treemap',
            events: {
                dataPointSelection: handleDataPointSelection
            },
            toolbar: {
                show: false
            },
            parentHeightOffset: 0,
        },
        grid: {
            padding: {
                top: 0,
                right: 0,
                bottom: 0,
                left: 0
            }
        },
        stroke: {
            show: true,
            width: 3,
            colors: ['#fff']
        },
        series: [{
            data: seriesData
        }],
        plotOptions: {
            treemap: {
                distributed: true,
                enableShades: false,
            }
        },
        states: {
            hover: {
                filter: {
                    type: 'darken',
                    value: 0.01,
                }
            },
            active: {
                filter: {
                    type: 'none'
                }
            }
        },
        dataLabels: {
            enabled: true,
            style: {
                fontSize: '12px',
                fontWeight: 500,
                colors: ['#000'],
            },
            dropShadow: {
                enabled: false
            },
            formatter: function (text, op) {
                const dataPoint = seriesData[op.dataPointIndex];
                if (!dataPoint) return text;

                const percentage = (dataPoint.y / totalValue) * 100;

                return [text, `${percentage.toFixed(1)}%`];
            },
            offsetY: -4
        },
        title: {
            text: categories && Object.keys(categories).length > 0
                ? `Consumer Grievance Categories${totalCounts ? ` (Total Complaints: ${totalCounts})` : ''}`
                : 'No Categories Found',
            align: 'center',
            style: {
                fontSize: '18px',
                fontWeight: 600,
                colors: ['#fff'],
            }
        },
        tooltip: {
            y: {
                formatter: (value, { seriesIndex, dataPointIndex, w }) => {
                    if (seriesData[dataPointIndex]) {
                        const count = seriesData[dataPointIndex].count;
                        const percentage = totalCounts > 0 ? ((count / totalCounts) * 100).toFixed(2) : 0;
                        return `Count: ${count.toLocaleString()}`;
                    }
                    return '';
                }
            }
        },
        legend: {
            show: false
        }
    };

    return (
        <Box sx={{
            position: 'relative',
            width: '100%',
            minHeight: '450px',
            height: '100%'
        }}>
            {isLoading && (
                <Box sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: 'rgba(255, 255, 255, 0.7)',
                    zIndex: 1
                }}>
                    <CircularProgress />
                </Box>
            )}
            <ReactApexChart
                options={chartOptions}
                series={chartOptions.series}
                type="treemap"
                height="100%"
            />
        </Box>
    );
};

export default TreemapChart;